import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    ArrowLeft,
    Download,
    FileText,
    Shield,
    Ship,
    Calendar,
    DollarSign,
    Users,
    Building,
    Clock,
    CheckCircle,
    AlertCircle,
    Printer,
    ExternalLink,
    TrendingUp,
    Award,
    FileCheck,
    Loader2,
    Eye,
    Copy,
    Share2
} from 'lucide-react';
import api from '../../services/api';
import Badge from '../../components/ui/Badge';

export default function PolicyView() {
    const { id } = useParams();
    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        const fetchPolicy = async () => {
            try {
                const { data } = await api.get(`/policies/${id}`);
                setPolicy(data);
            } catch (err) {
                setError('Failed to load policy details.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchPolicy();
    }, [id]);

    const downloadCertificate = async () => {
        setDownloading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                alert('Session expired. Please log in again.');
                return;
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/documents/policies/${id}/pdf`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Policy-${policy?.policy_number || id}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download error:', err);
            alert('Failed to download certificate. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const copyPolicyNumber = () => {
        navigator.clipboard?.writeText(policy?.policy_number || '');
        // Could add a toast notification here
    };

    if (loading) return <SkeletonLoader />;

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
                <div className="text-center max-w-md p-8">
                    <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                        <AlertCircle className="w-10 h-10 text-red-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Error Loading Policy</h3>
                    <p className="text-gray-500 dark:text-slate-400">{error}</p>
                    <Link
                        to="/policies"
                        className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#3a663f] text-white rounded-lg font-medium hover:bg-[#2d5231] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Policies
                    </Link>
                </div>
            </div>
        );
    }

    if (!policy) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
                <div className="text-center max-w-md p-8">
                    <div className="w-20 h-20 mx-auto bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-4">
                        <FileText className="w-10 h-10 text-gray-400 dark:text-slate-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Policy Not Found</h3>
                    <p className="text-gray-500 dark:text-slate-400">The requested policy could not be found.</p>
                    <Link
                        to="/policies"
                        className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#3a663f] text-white rounded-lg font-medium hover:bg-[#2d5231] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Policies
                    </Link>
                </div>
            </div>
        );
    }

    const coverageStart = new Date(policy.coverage_start);
    const coverageEnd = new Date(policy.coverage_end);
    const now = new Date();
    const totalDuration = coverageEnd - coverageStart;
    const elapsed = now - coverageStart;
    const progressPercent = Math.min(100, Math.max(0, Math.round((elapsed / totalDuration) * 100)));
    const isExpired = now > coverageEnd;
    const isActive = now >= coverageStart && now <= coverageEnd;
    const daysRemaining = Math.ceil((coverageEnd - now) / (1000 * 60 * 60 * 24));
    const daysElapsed = Math.ceil((now - coverageStart) / (1000 * 60 * 60 * 24));
    const totalDays = Math.ceil(totalDuration / (1000 * 60 * 60 * 24));

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Navigation */}
                <div className="flex items-center justify-between print:hidden">
                    <Link to="/policies" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#3a663f] dark:text-slate-400 dark:hover:text-[#4a7f4f] transition-colors font-medium">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Policies
                    </Link>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrint}
                            className="p-2 text-gray-500 hover:text-[#3a663f] dark:text-slate-400 dark:hover:text-[#4a7f4f] rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                            title="Print"
                        >
                            <Printer className="w-5 h-5" />
                        </button>
                        <button
                            onClick={copyPolicyNumber}
                            className="p-2 text-gray-500 hover:text-[#3a663f] dark:text-slate-400 dark:hover:text-[#4a7f4f] rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                            title="Copy Policy Number"
                        >
                            <Copy className="w-5 h-5" />
                        </button>
                        <button
                            onClick={downloadCertificate}
                            disabled={downloading}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-[#3a663f] hover:bg-[#2d5231] text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {downloading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Download className="w-4 h-4" />
                            )}
                            Download PDF
                        </button>
                    </div>
                </div>

                {/* Header Banner */}
                <div className="relative bg-gradient-to-br from-[#1e3b29] via-[#3a663f] to-[#4d8c54] rounded-2xl p-8 md:p-10 text-white overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                    <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/10 rounded-full translate-y-1/3 -translate-x-1/3" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />

                    <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 flex-wrap">
                                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                                    <Shield className="w-6 h-6" />
                                </div>
                                <span className="text-green-200 text-xs uppercase tracking-widest font-semibold">Policy</span>
                                <Badge
                                    status={policy.payment_status}
                                    className="bg-white/20 text-white border-0 text-xs px-3 py-1 rounded-full"
                                />
                                {isActive && !isExpired && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs font-medium text-green-100 border border-white/10">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse" />
                                        Active
                                    </span>
                                )}
                                {isExpired && (
                                    <span className="px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs font-medium text-red-200 border border-white/10">
                                        Expired
                                    </span>
                                )}
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
                                    {policy.policy_number}
                                </h1>
                                <p className="text-green-100/80 mt-1">{policy.insured_name || policy.client_name}</p>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-6 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                            <div>
                                <p className="text-green-200 text-xs">Sum Insured</p>
                                <p className="text-2xl font-bold">
                                    ${Number(policy.sum_insured).toLocaleString()}
                                </p>
                            </div>
                            <div className="w-px h-12 bg-white/20" />
                            <div>
                                <p className="text-green-200 text-xs">Premium</p>
                                <p className="text-2xl font-bold">
                                    ${Number(policy.total_amount).toLocaleString()}
                                </p>
                            </div>
                            <div className="w-px h-12 bg-white/20" />
                            <div>
                                <p className="text-green-200 text-xs">Issued</p>
                                <p className="font-bold">
                                    {new Date(policy.issued_date).toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column: Main Info */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Policy Details Card */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                            <div className="p-6 border-b border-gray-200 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                    <h2 className="font-bold text-gray-800 dark:text-white">Policy Information</h2>
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <DetailItem
                                        icon={Ship}
                                        label="Vessel"
                                        value={policy.vessel_name || 'N/A'}
                                    />
                                    <DetailItem
                                        icon={FileText}
                                        label="Product"
                                        value={policy.product_name || 'N/A'}
                                    />
                                    <DetailItem
                                        icon={DollarSign}
                                        label="Sum Insured"
                                        value={`$${Number(policy.sum_insured).toLocaleString()}`}
                                        highlight
                                    />
                                    <DetailItem
                                        icon={DollarSign}
                                        label="Premium"
                                        value={`$${Number(policy.total_amount).toLocaleString()}`}
                                        highlight
                                    />
                                    <DetailItem
                                        icon={DollarSign}
                                        label="Taxes"
                                        value={`$${Number(policy.taxes || 0).toLocaleString()}`}
                                    />
                                    <DetailItem
                                        icon={Badge}
                                        label="Payment Status"
                                        value={<Badge status={policy.payment_status} />}
                                    />
                                    {policy.insured_name && (
                                        <DetailItem
                                            icon={Users}
                                            label="Insured"
                                            value={policy.insured_name}
                                        />
                                    )}
                                    {policy.client_name && (
                                        <DetailItem
                                            icon={Users}
                                            label="Client"
                                            value={policy.client_name}
                                        />
                                    )}
                                    {policy.broker_name && (
                                        <DetailItem
                                            icon={Building}
                                            label="Broker"
                                            value={policy.broker_name}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Coverage Timeline Card */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                            <div className="p-6 border-b border-gray-200 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                    <h2 className="font-bold text-gray-800 dark:text-white">Coverage Period</h2>
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="flex items-center justify-between gap-4 mb-4">
                                    <div className="text-center">
                                        <p className="text-xs text-gray-400 dark:text-slate-500">Start</p>
                                        <p className="font-semibold text-gray-800 dark:text-white">
                                            {coverageStart.toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' })}
                                        </p>
                                    </div>
                                    <div className="flex-1">
                                        <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full relative overflow-hidden">
                                            <div
                                                className={`absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ${isExpired ? 'bg-red-500' : isActive ? 'bg-[#3a663f]' : 'bg-amber-500'
                                                    }`}
                                                style={{ width: `${Math.min(progressPercent, 100)}%` }}
                                            />
                                        </div>
                                        <div className="flex justify-between text-xs text-gray-400 dark:text-slate-500 mt-1">
                                            <span>{daysElapsed > 0 ? `${daysElapsed} days elapsed` : 'Not started'}</span>
                                            <span>{isExpired ? 'Expired' : `${daysRemaining} days remaining`}</span>
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs text-gray-400 dark:text-slate-500">End</p>
                                        <p className="font-semibold text-gray-800 dark:text-white">
                                            {coverageEnd.toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' })}
                                        </p>
                                    </div>
                                </div>

                                {/* Coverage Stats */}
                                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100 dark:border-slate-700">
                                    <div className="text-center">
                                        <p className="text-xs text-gray-400 dark:text-slate-500">Total Duration</p>
                                        <p className="font-semibold text-gray-800 dark:text-white">{totalDays} days</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs text-gray-400 dark:text-slate-500">Status</p>
                                        <p className={`font-semibold ${isExpired ? 'text-red-600 dark:text-red-400' :
                                                isActive ? 'text-green-600 dark:text-green-400' :
                                                    'text-amber-600 dark:text-amber-400'
                                            }`}>
                                            {isExpired ? 'Expired' : isActive ? 'Active' : 'Upcoming'}
                                        </p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs text-gray-400 dark:text-slate-500">Progress</p>
                                        <p className="font-semibold text-gray-800 dark:text-white">
                                            {Math.min(progressPercent, 100)}%
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quote Reference */}
                        {policy.quote_number && (
                            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                                <div className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <FileCheck className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                            <span className="text-sm text-gray-500 dark:text-slate-400">Generated from Quote</span>
                                        </div>
                                        <Link
                                            to={`/quotes/${policy.quote_id}`}
                                            className="inline-flex items-center gap-1 text-sm text-[#3a663f] dark:text-green-400 hover:text-[#2d5231] font-medium transition-colors"
                                        >
                                            {policy.quote_number}
                                            <ExternalLink className="w-3 h-3" />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column: Sidebar */}
                    <div className="space-y-6">
                        {/* Actions Card */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                            <div className="p-6 border-b border-gray-200 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                    <Award className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                    <h2 className="font-bold text-gray-800 dark:text-white">Actions</h2>
                                </div>
                            </div>
                            <div className="p-6 space-y-3">
                                <button
                                    onClick={downloadCertificate}
                                    disabled={downloading}
                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#3a663f] hover:bg-[#2d5231] text-white rounded-xl font-medium transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {downloading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <Download className="w-5 h-5" />
                                    )}
                                    Download Certificate
                                </button>
                                <button
                                    onClick={handlePrint}
                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-xl font-medium transition-colors"
                                >
                                    <Printer className="w-5 h-5" />
                                    Print Policy
                                </button>
                                <button
                                    onClick={copyPolicyNumber}
                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-xl font-medium transition-colors"
                                >
                                    <Copy className="w-5 h-5" />
                                    Copy Policy Number
                                </button>
                            </div>
                        </div>

                        {/* Policy Stats */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                            <div className="p-6 border-b border-gray-200 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                    <h2 className="font-bold text-gray-800 dark:text-white">Quick Stats</h2>
                                </div>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <p className="text-xs text-gray-400 dark:text-slate-500">Policy Age</p>
                                    <p className="font-semibold text-gray-800 dark:text-white">
                                        {Math.floor((now - new Date(policy.issued_date)) / (1000 * 60 * 60 * 24))} days
                                    </p>
                                </div>
                                {policy.payment_status === 'paid' && (
                                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
                                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                        <span className="text-sm text-green-700 dark:text-green-400 font-medium">Payment Confirmed</span>
                                    </div>
                                )}
                                {policy.payment_status === 'unpaid' && (
                                    <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                                        <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                        <span className="text-sm text-amber-700 dark:text-amber-400 font-medium">Payment Pending</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Navigation */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                            <div className="p-6">
                                <Link
                                    to="/policies"
                                    className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-slate-400 hover:text-[#3a663f] dark:hover:text-green-400 transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Back to All Policies
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ========== SUB-COMPONENTS ==========

function DetailItem({ icon: Icon, label, value, highlight }) {
    if (!value && value !== 0) return null;

    return (
        <div className="flex items-start gap-3">
            <div className="p-2 bg-[#3a663f]/10 dark:bg-[#3a663f]/20 rounded-lg">
                <Icon className="w-4 h-4 text-[#3a663f] dark:text-green-400" />
            </div>
            <div>
                <p className="text-xs text-gray-400 dark:text-slate-500 uppercase tracking-wider">{label}</p>
                <div className={`text-sm font-medium mt-0.5 ${highlight
                        ? 'text-[#3a663f] dark:text-green-400 font-bold text-base'
                        : 'text-gray-800 dark:text-white'
                    }`}>
                    {value}
                </div>
            </div>
        </div>
    );
}

// ========== SKELETON LOADER ==========
function SkeletonLoader() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="animate-pulse space-y-6">
                    <div className="flex items-center justify-between">
                        <div className="h-10 bg-gray-200 dark:bg-slate-700 rounded-lg w-32" />
                        <div className="h-10 bg-gray-200 dark:bg-slate-700 rounded-lg w-32" />
                    </div>
                    <div className="h-48 bg-gray-200 dark:bg-slate-700 rounded-2xl" />
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 space-y-6">
                            <div className="h-64 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                            <div className="h-48 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                        </div>
                        <div className="space-y-6">
                            <div className="h-48 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                            <div className="h-32 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}